import { createClient } from '@supabase/supabase-js';
import { 
  SUPABASE_URL, 
  SUPABASE_ANON_KEY, 
  SUPABASE_BUCKET
} from '../config.js';

// Check for required Supabase configuration
if (!SUPABASE_URL || !SUPABASE_ANON_KEY) {
  console.error('❌ Missing required Supabase configuration!');
  console.error('SUPABASE_URL:', SUPABASE_URL ? 'Set' : 'Missing');
  console.error('SUPABASE_ANON_KEY:', SUPABASE_ANON_KEY ? 'Set' : 'Missing');
  
  // In development, provide more helpful error message
  if (process.env.NODE_ENV === 'development') {
    console.error('🔧 For local development, check that the config.js file has fallback values');
    console.error('🔧 Or create a .env file in the frontend directory with your Supabase credentials');
  }
  
  throw new Error('Missing required Supabase configuration. Please check your environment variables.');
}

// Debug: Log the configuration being used
console.log('🔧 Supabase Client Configuration:');
console.log('URL:', SUPABASE_URL);
console.log('Anon Key (first 20 chars):', SUPABASE_ANON_KEY ? SUPABASE_ANON_KEY.substring(0, 20) + '...' : 'Missing');
console.log('Environment:', process.env.NODE_ENV);

// Singleton pattern for browser (prevents multiple instances during hot reload)
const getSupabase = () => {
  if (typeof window !== 'undefined') {
    if (!window.__supabase) {
      console.log('🔧 Creating new Supabase client with:', {
        url: SUPABASE_URL,
        hasAnonKey: !!SUPABASE_ANON_KEY
      });
      window.__supabase = createClient(SUPABASE_URL, SUPABASE_ANON_KEY);
    }
    return window.__supabase;
  }
  return createClient(SUPABASE_URL, SUPABASE_ANON_KEY);
};

// Note: Admin client should only be used in backend/server-side code
// Frontend should only use the regular client for security reasons
const supabase = getSupabase();

// Export the supabase client as default
export default supabase;

/**
 * Upload multiple files to Supabase Storage with metadata.
 * @param {File[]} files - Array of File objects
 * @param {string} userName - Name of the uploader
 * @returns {Promise<Array<{fileName: string, url: string, error?: string}>>}
 */
export async function uploadFilesWithMetadata(files, userName) {
  if (!SUPABASE_BUCKET) throw new Error('SUPABASE_BUCKET not set');
  
  const results = [];
  for (const file of files) {
    try {
      // Sanitize user name for file path (remove spaces and special chars)
      const sanitizedUserName = (userName || 'anonymous').replace(/[^a-zA-Z0-9]/g, '_');
      
      // Create a safe file path
      const timestamp = Date.now();
      const safeFileName = file.name.replace(/[^a-zA-Z0-9.-]/g, '_');
      const filePath = `${sanitizedUserName}/${timestamp}_${safeFileName}`;
      
      console.log('Uploading file to Supabase:', {
        fileName: file.name,
        filePath: filePath,
        fileSize: file.size,
        fileType: file.type,
        bucket: SUPABASE_BUCKET
      });
      
      // Use regular client (RLS policies will apply)
      const { error } = await supabase.storage.from(SUPABASE_BUCKET).upload(filePath, file, {
        cacheControl: '3600',
        upsert: false,
      });
      
      if (error) {
        console.error('Supabase upload error:', error);
        
        // Provide specific error messages for common issues
        if (error.message.includes('row-level security policy')) {
          results.push({ 
            fileName: file.name, 
            error: `Upload failed: RLS policy violation. Please check bucket permissions.` 
          });
        } else if (error.message.includes('Unauthorized')) {
          results.push({ 
            fileName: file.name, 
            error: `Upload failed: Unauthorized. Please check authentication.` 
          });
        } else {
          results.push({ 
            fileName: file.name, 
            error: `Upload failed: ${error.message}` 
          });
        }
      } else {
        const url = supabase.storage.from(SUPABASE_BUCKET).getPublicUrl(filePath).data.publicUrl;
        console.log('File uploaded successfully:', url);
        results.push({ fileName: file.name, url });
      }
    } catch (err) {
      console.error('Unexpected error during upload:', err);
      results.push({ 
        fileName: file.name, 
        error: `Unexpected error: ${err.message}` 
      });
    }
  }
  return results;
} 